// src/page/AuthPage.tsx

import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "../supabaseClient";
import { Eye, EyeOff } from "lucide-react";
import logo from "@/assets/logo-circle.png";
import { useTranslation } from "react-i18next";

export default function AuthPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [mode, setMode] = useState<"signin" | "signup">("signin");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [pwdVisible, setPwdVisible] = useState(false);

  const canSubmit =
    email.trim().length > 3 && password.trim().length > 3 && !loading;

  // Google 登录
  async function handleGoogleSignIn() {
    try {
      setLoading(true);
      const { error } = await supabase.auth.signInWithOAuth({
        provider: "google",
        options: {
          // ⭐ 登录成功后直接回到 /main
          redirectTo:  `${window.location.origin}/main`,
        },
      });
      if (error) {
        console.error("Google sign-in error:", error);
        window.alert("Google 登录失败: " + error.message);
      }
      // 注意：OAuth 会整页重定向，成功后由 Welcome + ProtectedRoute 决定去哪里
    } catch (e) {
      console.error("Google sign-in exception:", e);
    } finally {
      setLoading(false);
    }
  }

  // Email / Password 登录
  async function handleSignIn() {
    try {
      setLoading(true);
      const { error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });
      if (error) {
        window.alert("Sign In Error: " + error.message);
      } else {
        // ✅ 登录成功后，直接去主页面 /main
        navigate("/main", { replace: true });
      }
    } finally {
      setLoading(false);
    }
  }

  // 注册
  async function handleSignUp() {
    try {
      setLoading(true);
      const { error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: window.location.origin + "/", // 邮件确认后回到首页（之后仍会进 Welcome 流程）
        },
      });
      if (error) {
        window.alert("Sign Up Error: " + error.message);
      } else {
        window.alert(
          "Registration successful! Please check your email to confirm."
        );
      }
    } finally {
      setLoading(false);
    }
  }

  const submit = () => {
    if (mode === "signup") handleSignUp();
    else handleSignIn();
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-soft p-4">
      <div className="w-full max-w-md bg-background rounded-2xl shadow-[var(--shadow-soft)] p-6">
        {/* Logo + 标题 */}
        <div className="text-center mb-6">
          <img
            src={logo}
            alt={t('app.name', { defaultValue: 'Inner Gem' })}
            className="w-16 h-16 mx-auto mb-4 rounded-full"
          />
          <h1 className="text-2xl font-bold text-primary">
            {t('auth.welcome', { defaultValue: 'Welcome to Inner Gem' })}
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            {t('auth.tagline', { defaultValue: 'Your journey to self-discovery awaits' })}
          </p>
        </div>

        {/* Google 登录 */}
        <button
          type="button"
          onClick={handleGoogleSignIn}
          disabled={loading}
          className="w-full h-11 mb-4 flex items-center justify-center rounded-xl border border-border bg-background text-sm font-medium hover:bg-accent disabled:opacity-60"
        >
          <span className="mr-2 text-lg">G</span>
          {t('auth.google', { defaultValue: 'Continue with Google' })}
        </button>

        {/* 分隔线 */}
        <div className="flex items-center my-4">
          <div className="flex-1 h-px bg-border" />
          <span className="mx-3 text-xs text-muted-foreground">{t('auth.or', { defaultValue: 'OR' })}</span>
          <div className="flex-1 h-px bg-border" />
        </div>

        {/* Tab 切换 Login / Register */}
        <div className="flex mb-4 rounded-full bg-muted p-1">
          <button
            type="button"
            onClick={() => setMode("signin")}
            className={`flex-1 h-9 text-sm rounded-full ${
              mode === "signin"
                ? "bg-background shadow-sm font-semibold"
                : "text-muted-foreground"
            }`}
          >
            {t('auth.login', { defaultValue: 'Login' })}
          </button>
          <button
            type="button"
            onClick={() => setMode("signup")}
            className={`flex-1 h-9 text-sm rounded-full ${
              mode === "signup"
                ? "bg-background shadow-sm font-semibold"
                : "text-muted-foreground"
            }`}
          >
            {t('auth.register', { defaultValue: 'Register' })}
          </button>
        </div>

        {/* Email / Password 表单 */}
        <form
          onSubmit={(e) => {
            e.preventDefault();
            submit();
          }}
          className="space-y-3"
        >
          <div className="space-y-1">
            <label className="text-sm font-medium text-foreground">
              {t('auth.email', { defaultValue: 'Email' })}
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full h-10 rounded-xl border border-input bg-muted/40 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
              placeholder={t('auth.email_placeholder', { defaultValue: 'you@example.com' })}
              autoCapitalize="none"
            />
          </div>

          <div className="space-y-1">
            <label className="text-sm font-medium text-foreground">
              {t('auth.password', { defaultValue: 'Password' })}
            </label>
            <div className="relative">
              <input
                type={pwdVisible ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full h-10 rounded-xl border border-input bg-muted/40 px-3 pr-10 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                placeholder={t('auth.password_placeholder', { defaultValue: 'Enter your password' })}
              />
              <button
                type="button"
                onClick={() => setPwdVisible((v) => !v)}
                className="absolute inset-y-0 right-0 flex w-10 items-center justify-center text-muted-foreground"
              >
                {pwdVisible ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>

          <button
            type="submit"
            disabled={!canSubmit}
            className="mt-4 w-full h-11 rounded-full bg-gradient-to-r from-primary to-primary/80 text-primary-foreground text-sm font-semibold disabled:opacity-60"
          >
            {loading
              ? mode === "signin"
                ? t('auth.signing_in', { defaultValue: 'Signing in...' })
                : t('auth.creating_account', { defaultValue: 'Creating account...' })
              : mode === "signin"
              ? t('auth.sign_in', { defaultValue: 'Sign In' })
              : t('auth.create_account', { defaultValue: 'Create Account' })}
          </button>
        </form>
      </div>
    </div>
  );
}
